/**
 * CitationExporter Component (DC-1066)
 * ============================================================================
 * Dropdown button for exporting citations in various formats.
 * Supports both regulatory and literature citations.
 *
 * @version 1.0.0
 * @created 2026-01-25
 * ============================================================================
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { RegulatoryCitation, CitationType } from '../../types/evidence';
import type { LiteratureCitationRef } from '../../types/bridgeIntelligence';
import './citation-exporter.css';

// ============================================================================
// Types
// ============================================================================

export type ExportFormat = 'bibtex' | 'ris' | 'json' | 'markdown';

export interface CitationExporterProps {
  /** Citations to export */
  citations: (RegulatoryCitation | LiteratureCitationRef)[];
  /** Citation type */
  citationType: CitationType | 'mixed';
  /** Disabled state */
  disabled?: boolean;
  /** Custom className */
  className?: string;
}

interface ExportFormatOption {
  format: ExportFormat;
  label: string;
  icon: string;
  description: string;
  extension: string;
}

// ============================================================================
// Constants
// ============================================================================

const EXPORT_FORMATS: ExportFormatOption[] = [
  {
    format: 'bibtex',
    label: 'BibTeX',
    icon: '@',
    description: 'For LaTeX and reference managers',
    extension: 'bib',
  },
  {
    format: 'ris',
    label: 'RIS',
    icon: 'R',
    description: 'For EndNote, Zotero, Mendeley',
    extension: 'ris',
  },
  {
    format: 'json',
    label: 'JSON',
    icon: '{ }',
    description: 'Machine-readable format',
    extension: 'json',
  },
  {
    format: 'markdown',
    label: 'Markdown',
    icon: 'M',
    description: 'For documentation',
    extension: 'md',
  },
];

// ============================================================================
// Export Formatters
// ============================================================================

function isLiteratureCitation(c: RegulatoryCitation | LiteratureCitationRef): c is LiteratureCitationRef {
  return 'pmid' in c;
}

function isRegulatoryCitation(c: RegulatoryCitation | LiteratureCitationRef): c is RegulatoryCitation {
  return 'framework' in c;
}

function formatBibTeX(citations: (RegulatoryCitation | LiteratureCitationRef)[]): string {
  return citations.map((c, index) => {
    if (isLiteratureCitation(c)) {
      const key = `citation_${c.pmid}`;
      return `@article{${key},
  title = {${c.title}},
  author = {${c.authors}},
  journal = {${c.journal || 'Unknown'}},
  year = {${c.year || 'n.d.'}},
  pmid = {${c.pmid}},
  doi = {${c.doi || ''}}
}`;
    } else {
      const key = `reg_${c.framework.toLowerCase()}_${index}`;
      return `@misc{${key},
  title = {${c.document}},
  author = {{${c.framework}}},
  note = {Section ${c.section}},
  howpublished = {\\url{${c.url}}}
}`;
    }
  }).join('\n\n');
}

function formatRIS(citations: (RegulatoryCitation | LiteratureCitationRef)[]): string {
  return citations.map(c => {
    if (isLiteratureCitation(c)) {
      return `TY  - JOUR
TI  - ${c.title}
AU  - ${c.authors}
JO  - ${c.journal || ''}
PY  - ${c.year || ''}
AN  - PMID:${c.pmid}
DO  - ${c.doi || ''}
ER  -`;
    } else {
      return `TY  - GEN
TI  - ${c.document}
AU  - ${c.framework}
N1  - Section ${c.section}
UR  - ${c.url}
ER  -`;
    }
  }).join('\n\n');
}

function formatJSON(citations: (RegulatoryCitation | LiteratureCitationRef)[]): string {
  return JSON.stringify(citations, null, 2);
}

function formatMarkdown(citations: (RegulatoryCitation | LiteratureCitationRef)[]): string {
  const lines: string[] = ['# Citations\n'];

  const literature = citations.filter(isLiteratureCitation);
  const regulatory = citations.filter(isRegulatoryCitation);

  if (literature.length > 0) {
    lines.push('## Literature Citations\n');
    literature.forEach((c, i) => {
      lines.push(`${i + 1}. **${c.title}**`);
      lines.push(`   ${c.authors}. *${c.journal || 'Unknown'}*. ${c.year || 'n.d.'}`);
      lines.push(`   PMID: [${c.pmid}](https://pubmed.ncbi.nlm.nih.gov/${c.pmid})`);
      if (c.doi) {
        lines.push(`   DOI: [${c.doi}](https://doi.org/${c.doi})`);
      }
      lines.push('');
    });
  }

  if (regulatory.length > 0) {
    lines.push('## Regulatory Citations\n');
    regulatory.forEach((c, i) => {
      lines.push(`${i + 1}. **${c.framework} - ${c.document}**`);
      lines.push(`   Section: ${c.section}`);
      lines.push(`   > "${c.text}"`);
      lines.push(`   [View Source](${c.url})`);
      lines.push('');
    });
  }

  return lines.join('\n');
}

// ============================================================================
// Component
// ============================================================================

export const CitationExporter: React.FC<CitationExporterProps> = ({
  citations,
  citationType,
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<'idle' | 'exporting' | 'success' | 'error'>('idle');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleExport = useCallback(async (format: ExportFormat) => {
    if (disabled || citations.length === 0 || status === 'exporting') return;

    setIsOpen(false);
    setStatus('exporting');

    try {
      let content: string;
      let mimeType: string;

      switch (format) {
        case 'bibtex':
          content = formatBibTeX(citations);
          mimeType = 'text/plain';
          break;
        case 'ris':
          content = formatRIS(citations);
          mimeType = 'text/plain';
          break;
        case 'json':
          content = formatJSON(citations);
          mimeType = 'application/json';
          break;
        case 'markdown':
          content = formatMarkdown(citations);
          mimeType = 'text/markdown';
          break;
        default:
          throw new Error(`Unknown format: ${format}`);
      }

      // Create and download file
      const formatInfo = EXPORT_FORMATS.find(f => f.format === format);
      const filename = `citations-${citationType}-${Date.now()}.${formatInfo?.extension || format}`;

      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setStatus('success');
      setTimeout(() => setStatus('idle'), 2000);
    } catch (err) {
      console.error('Export failed:', err);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  }, [citations, citationType, disabled, status]);

  // ============================================================================
  // Render
  // ============================================================================

  const isEmpty = citations.length === 0;
  const isDisabled = disabled || isEmpty;

  const getButtonLabel = (): string => {
    switch (status) {
      case 'exporting': return 'Exporting...';
      case 'success': return 'Downloaded!';
      case 'error': return 'Failed';
      default: return 'Export';
    }
  };

  return (
    <div ref={dropdownRef} className={`citation-exporter ${className}`}>
      <button
        className={`citation-exporter__button citation-exporter__button--${status}`}
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        disabled={isDisabled || status === 'exporting'}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={isEmpty ? 'No citations to export' : 'Export citations'}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        <span>{getButtonLabel()}</span>
        {status === 'idle' && (
          <span className={`citation-exporter__caret ${isOpen ? 'open' : ''}`}>
            {isOpen ? '▲' : '▼'}
          </span>
        )}
      </button>

      {isOpen && status === 'idle' && (
        <div className="citation-exporter__dropdown" role="listbox">
          <div className="citation-exporter__dropdown-header">
            Export {citations.length} citation{citations.length !== 1 ? 's' : ''}
          </div>
          {EXPORT_FORMATS.map(({ format, label, icon, description }) => (
            <button
              key={format}
              className="citation-exporter__item"
              onClick={() => handleExport(format)}
              role="option"
            >
              <span className="citation-exporter__item-icon">{icon}</span>
              <div className="citation-exporter__item-content">
                <span className="citation-exporter__item-label">{label}</span>
                <span className="citation-exporter__item-description">{description}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CitationExporter;
